import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Shield, Clock, AlertTriangle, CheckCircle,
  Eye, Trash2, Calendar, Upload, Search,
} from 'lucide-react';
import api from '../utils/api';
import { License, ComplianceDocument, AuditLog, BatchTraceability, Batch } from '../types/modules';

const LICENSE_TYPES = ['TTB', 'State', 'Local', 'Food Safety', 'Other'];
const DOCUMENT_CATEGORIES = ['License', 'SDS', 'Certificate', 'Report', 'Other'];

const Compliance: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('licenses');
  const [loading, setLoading] = useState<boolean>(false);
  
  // Licenses state
  const [licenses, setLicenses] = useState<License[]>([]);
  const [showLicenseModal, setShowLicenseModal] = useState<boolean>(false);
  const [licenseForm, setLicenseForm] = useState<{
    license_type: string;
    license_number: string;
    issuing_authority: string;
    issue_date: string;
    expiration_date: string;
    status: string;
    notes: string;
  }>({
    license_type: 'TTB',
    license_number: '',
    issuing_authority: '',
    issue_date: '',
    expiration_date: '',
    status: 'Active',
    notes: '',
  });
  
  // Documents state
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
  const [documentForm, setDocumentForm] = useState<{
    name: string;
    category: string;
    description: string;
    file_data: string;
    file_type: string;
  }>({
    name: '',
    category: 'License',
    description: '',
    file_data: '',
    file_type: '',
  });
  
  // Batch Traceability state
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [traceabilityData, setTraceabilityData] = useState<BatchTraceability | null>(null);
  
  // Audit Logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'licenses') {
        const res = await api.get('/compliance/licenses');
        setLicenses(res.data);
      } else if (activeTab === 'documents') {
        const res = await api.get('/compliance/documents');
        setDocuments(res.data);
      } else if (activeTab === 'traceability') {
        // Fetch batches for selection
        const res = await api.get('/batches/');
        setBatches(res.data.items || res.data);
      } else if (activeTab === 'audit') {
        const res = await api.get('/compliance/audit-logs?limit=50');
        setAuditLogs(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch compliance data:', err);
    } finally {
      setLoading(false);
    }
  };

  // License handlers
  const createLicense = async () => {
    try {
      await api.post('/compliance/licenses', licenseForm);
      setShowLicenseModal(false);
      fetchData();
      resetLicenseForm();
    } catch (err) {
      console.error('Failed to create license:', err);
      alert('Failed to create license');
    }
  };

  const deleteLicense = async (licenseId) => {
    if (!confirm('Are you sure you want to delete this license?')) return;
    
    try {
      await api.delete(`/compliance/licenses/${licenseId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to delete license:', err);
    }
  };

  const resetLicenseForm = () => {
    setLicenseForm({
      license_type: 'TTB',
      license_number: '',
      issuing_authority: '',
      issue_date: '',
      expiration_date: '',
      status: 'Active',
      notes: '',
    });
  };

  // Document handlers
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      setDocumentForm({
        ...documentForm,
        file_data: typeof result === 'string' ? result : '',
        file_type: file.type,
      });
    };
    reader.readAsDataURL(file);
  };

  const createDocument = async () => {
    try {
      await api.post('/compliance/documents', documentForm);
      setShowDocumentModal(false);
      fetchData();
      resetDocumentForm();
    } catch (err) {
      console.error('Failed to upload document:', err);
      alert('Failed to upload document');
    }
  };

  const deleteDocument = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/compliance/documents/${documentId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  const resetDocumentForm = () => {
    setDocumentForm({
      name: '',
      category: 'License',
      description: '',
      file_data: '',
      file_type: '',
    });
  };

  // Batch Traceability handlers
  const viewBatchTrace = async (batchId: string): Promise<void> => {
    try {
      const res = await api.get(`/compliance/batch-trace/${batchId}`);
      setTraceabilityData(res.data);
      setSelectedBatch(batchId);
    } catch (err) {
      console.error('Failed to fetch traceability:', err);
      alert('Failed to load batch traceability');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Compliance</h1>
        <p className="text-gray-500 mt-1">Track licenses, documents, batch traceability, and audit logs</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max sm:min-w-0">
            {[
              { id: 'licenses', label: 'Licenses & Permits', icon: Shield },
              { id: 'traceability', label: 'Batch Traceability', icon: Search },
              { id: 'documents', label: 'Documents', icon: FileText },
              { id: 'audit', label: 'Audit Trail', icon: Clock },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#0891B2] text-[#0891B2] bg-cyan-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#0891B2] border-r-transparent"></div>
              <p className="text-gray-500 mt-2">Loading...</p>
            </div>
          ) : (
            <>
              {/* Licenses Tab */}
              {activeTab === 'licenses' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Licenses & Permits</h3>
                    <button
                      onClick={() => setShowLicenseModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg text-sm font-medium"
                    >
                      <Plus size={16} />
                      Add License
                    </button>
                  </div>

                  {licenses.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No licenses yet. Add your first license to get started.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {licenses.map((license) => (
                        <div
                          key={license.id}
                          className={`border rounded-lg p-4 ${
                            license.status === 'Expired'
                              ? 'border-red-300 bg-red-50'
                              : license.status === 'Expiring Soon'
                              ? 'border-orange-300 bg-orange-50'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">{license.license_type}</h4>
                              <p className="text-sm text-gray-500">{license.license_number}</p>
                            </div>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                license.status === 'Active'
                                  ? 'bg-green-100 text-green-800'
                                  : license.status === 'Expiring Soon'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {license.status}
                            </span>
                          </div>
                          
                          <div className="space-y-1 text-sm text-gray-600 mb-3">
                            <p>Authority: {license.issuing_authority}</p>
                            <p>Issued: {new Date(license.issue_date).toLocaleDateString()}</p>
                            <p>Expires: {new Date(license.expiration_date).toLocaleDateString()}</p>
                            {license.days_until_expiration !== null && (
                              <p className={license.days_until_expiration < 30 ? 'text-orange-600 font-medium' : ''}>
                                {license.days_until_expiration > 0
                                  ? `${license.days_until_expiration} days remaining`
                                  : `Expired ${Math.abs(license.days_until_expiration)} days ago`}
                              </p>
                            )}
                          </div>

                          <button
                            onClick={() => deleteLicense(license.id)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={14} className="inline mr-1" />
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Batch Traceability Tab */}
              {activeTab === 'traceability' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Batch Traceability</h3>

                  {!traceabilityData ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-4">
                        Select a batch to view full traceability details
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {batches.map((batch) => (
                          <button
                            key={batch.id}
                            onClick={() => viewBatchTrace(batch.id)}
                            className="border border-gray-200 rounded-lg p-4 hover:border-[#0891B2] hover:bg-cyan-50 text-left transition-colors"
                          >
                            <h4 className="font-semibold text-gray-900">{batch.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{batch.style}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              Brewed: {new Date(batch.brewDate).toLocaleDateString()}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        onClick={() => setTraceabilityData(null)}
                        className="mb-4 text-sm text-[#0891B2] hover:text-[#0891B2]/80"
                      >
                        ← Back to batch list
                      </button>

                      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 mb-6">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          {traceabilityData.batch_name}
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Style</p>
                            <p className="font-semibold">{traceabilityData.style}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Brew Date</p>
                            <p className="font-semibold">
                              {new Date(traceabilityData.brew_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Status</p>
                            <p className="font-semibold">{traceabilityData.status}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Brewmaster</p>
                            <p className="font-semibold">{traceabilityData.created_by}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Ingredients */}
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold mb-3">Ingredients</h4>
                          {traceabilityData.ingredients.length > 0 ? (
                            <ul className="space-y-2 text-sm">
                              {traceabilityData.ingredients.map((ing, idx) => (
                                <li key={idx} className="flex justify-between">
                                  <span>{ing.name}</span>
                                  <span className="text-gray-500">{ing.quantity}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500">No ingredients recorded</p>
                          )}
                        </div>

                        {/* Quality Checks */}
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold mb-3">Quality Checks</h4>
                          <ul className="space-y-3">
                            {traceabilityData.quality_checks.map((check, idx) => (
                              <li key={idx} className="text-sm">
                                <div className="flex items-center gap-2 mb-1">
                                  <CheckCircle size={16} className="text-green-600" />
                                  <span className="font-medium">{check.checkpoint}</span>
                                </div>
                                <p className="text-gray-600 ml-6">{check.notes}</p>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Related Orders */}
                        {traceabilityData.related_orders.length > 0 && (
                          <div className="border border-gray-200 rounded-lg p-4 lg:col-span-2">
                            <h4 className="font-semibold mb-3">Distribution History</h4>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                      Order ID
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                      Customer
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                      Date
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                      Quantity
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {traceabilityData.related_orders.map((order, idx) => (
                                    <tr key={idx}>
                                      <td className="px-3 py-2 text-gray-900">
                                        {order.order_id.slice(0, 8)}...
                                      </td>
                                      <td className="px-3 py-2 text-gray-900">{order.customer}</td>
                                      <td className="px-3 py-2 text-gray-500">
                                        {new Date(order.date).toLocaleDateString()}
                                      </td>
                                      <td className="px-3 py-2 text-gray-900 text-right">
                                        {order.quantity}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Compliance Documents</h3>
                    <button
                      onClick={() => setShowDocumentModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg text-sm font-medium"
                    >
                      <Upload size={16} />
                      Upload Document
                    </button>
                  </div>

                  {documents.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No documents yet. Upload your first compliance document.
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Category
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Uploaded By
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Upload Date
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {documents.map((doc) => (
                              <tr key={doc.id}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {doc.name}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    {doc.category}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">{doc.uploaded_by}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(doc.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  <button
                                    onClick={() => deleteDocument(doc.id)}
                                    className="p-1 text-gray-600 hover:text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Audit Trail Tab */}
              {activeTab === 'audit' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Audit Trail</h3>

                  {auditLogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No audit logs yet. Actions will be logged here.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {auditLogs.map((log) => (
                        <div
                          key={log.id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">{log.user_name}</span>
                                <span
                                  className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                    log.action === 'created'
                                      ? 'bg-green-100 text-green-800'
                                      : log.action === 'updated'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {log.action}
                                </span>
                                <span className="text-sm text-gray-600">{log.resource_type}</span>
                              </div>
                              <p className="text-sm text-gray-500">
                                {new Date(log.timestamp).toLocaleString()}
                              </p>
                              {Object.keys(log.details).length > 0 && (
                                <div className="mt-2 text-xs text-gray-500">
                                  <code className="bg-gray-100 px-2 py-1 rounded">
                                    {JSON.stringify(log.details, null, 2)}
                                  </code>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add License Modal */}
      {showLicenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Add License / Permit</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Type *
                  </label>
                  <select
                    value={licenseForm.license_type}
                    onChange={(e) => setLicenseForm({ ...licenseForm, license_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  >
                    {LICENSE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Number *
                  </label>
                  <input
                    type="text"
                    value={licenseForm.license_number}
                    onChange={(e) => setLicenseForm({ ...licenseForm, license_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issuing Authority *
                </label>
                <input
                  type="text"
                  value={licenseForm.issuing_authority}
                  onChange={(e) => setLicenseForm({ ...licenseForm, issuing_authority: e.target.value })}
                  placeholder="e.g., TTB, State Alcohol Board"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    value={licenseForm.issue_date}
                    onChange={(e) => setLicenseForm({ ...licenseForm, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Date *
                  </label>
                  <input
                    type="date"
                    value={licenseForm.expiration_date}
                    onChange={(e) => setLicenseForm({ ...licenseForm, expiration_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={licenseForm.notes}
                  onChange={(e) => setLicenseForm({ ...licenseForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowLicenseModal(false);
                  resetLicenseForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createLicense}
                className="px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg"
              >
                Add License
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Upload Compliance Document</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Name *
                </label>
                <input
                  type="text"
                  value={documentForm.name}
                  onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={documentForm.category}
                  onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                >
                  {DOCUMENT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File * (Max 5MB)
                </label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={documentForm.description}
                  onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDocumentModal(false);
                  resetDocumentForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createDocument}
                className="px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg"
              >
                Upload Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compliance;
